/**
 * The member's own words about a restaurant — one limit, shared by every note
 * editor in the app.
 *
 * Notes are Dining Profile entries now (RM-294), and RM AI saves what the
 * member says VERBATIM, up to ProfileEntry.SOURCE_TEXT_MAX. The editors used
 * to cap at 500, the old FavoriteNote limit: a captured note longer than that
 * opened in a field the member could delete from but never add to. Keep this
 * in step with SOURCE_TEXT_MAX in models/ProfileEntry.js.
 */
export const NOTE_MAX_LENGTH = 2000

/**
 * The approved wording of a share, which is its own text and is capped
 * separately from the private entry. Keep in step with SHARE_TEXT_MAX in
 * models/ProfileShare.js.
 */
export const SHARE_MAX_LENGTH = 1000

/*
 * A note is shown exactly as the member wrote it, restaurant name included
 * (Kapil, 2026-09-29, RM-346: "if the member includes the restaurant name in
 * their note/highlight, so should we"). This replaces the 2026-09-18 rule that
 * dropped a leading name under the restaurant's own heading — it turned
 * "Galit's hummus is incredible" into "'s hummus is incredible".
 */

/**
 * What a note of the member's own IS, in Kapil's words (2026-09-22,
 * 2026-09-23): "Private Only", "Highlight shared with your Network",
 * "Highlight shared with your Network & {Restaurant}" (the restaurant's name,
 * like "Shared with {Restaurant}"), or "Shared with {Restaurant}" for a note
 * that went only to the restaurant — not a Highlight their network sees. Once
 * the member said yes to the restaurant showing it, it is a Member Highlight
 * on that restaurant's RM page, which anyone can see. ONE source for My
 * Notes, the Note pop-up and the Favorites row, so the three can never
 * disagree about the same note.
 *
 * `audiences` are the note's ACTIVE share audiences.
 */
export function noteShareStatus(audiences, restaurantName) {
  const list = Array.isArray(audiences) ? audiences : []
  const place = restaurantName || 'the restaurant'
  if (!list.length) return 'Private Only'
  if (list.includes('public')) return `Member Highlight on ${place}'s RM page`
  if (list.includes('network')) {
    return list.includes('restaurant') ? `Highlight shared with your Network & ${place}` : 'Highlight shared with your Network'
  }

  return `Shared with ${place}`
}

/**
 * A note cut short for a one-line list, the way Kapil wrote it (2026-09-23):
 * "Great energy and ambiance - patio is amazing too…", "the booths along the
 * patio are the ones to ask for…". The FIRST line only, ended at a whole word,
 * trailing punctuation dropped, then "…" — and "…" only when something was cut.
 */
export function noteSnippet(text, max = 56) {
  const all = String(text || '').trim()
  const lines = all.split(/\n+/)
  const first = (lines[0] || '').trim()
  const more = lines.length > 1 || first.length > max
  if (!more) return first

  let cut = first.length > max ? first.slice(0, max) : first
  if (first.length > max && cut.lastIndexOf(' ') > 0) cut = cut.slice(0, cut.lastIndexOf(' '))

  return `${cut.replace(/[\s,;:.\-–—]+$/, '')}…`
}

